"use client";

import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useState, useMemo, useEffect, useContext, createContext } from "react";
import { ExternalLink, Video, Github, X, Filter, Code2 } from "lucide-react";
import { File, Folder, Tree, type TreeViewElement } from "@/ui/file-tree";

import { cn } from "@/utils";

import { projects, projectsIntro, type Project } from "@/constants";
import { fadeIn, staggerContainer, textVariant } from "@/utils/motion";
import { Section } from "@/ui/organism/section";
import styles from "@/ui/styles/shared.module.css";

// Create a context to track selection
const SelectionContext = createContext<{
  selectedId: string | undefined;
  setSelectedId: (id: string | undefined) => void;
} | null>(null);

const tagStyles = {
  blue: "bg-[var(--tag-blue-bg)] text-[var(--tag-blue-text)] border-blue-400/20",
  green: "bg-[var(--tag-emerald-bg)] text-[var(--tag-emerald-text)] border-emerald-400/20",
  yellow: "bg-[var(--tag-amber-bg)] text-[var(--tag-amber-text)] border-amber-300/25",
  red: "bg-[var(--tag-red-bg)] text-[var(--tag-red-text)] border-red-400/20",
  orange: "bg-[var(--tag-orange-bg)] text-[var(--tag-orange-text)] border-orange-400/20",
};

// Organize projects by technology/framework
const organizeProjectsByTech = (projects: Project[]) => {
  const categories: Record<string, Project[]> = {
    "Next.js": [],
    React: [],
    Vue: [],
    Wordpress: [],
    Desktop: [],
    Games: [],
    Other: [],
  };

  projects.forEach((project) => {
    const tags = project.tags.map((t) => t.name.toLowerCase());

    if (tags.some((t) => t.includes("next"))) {
      categories["Next.js"].push(project);
    } else if (tags.some((t) => t.includes("react") && !t.includes("next"))) {
      categories["React"].push(project);
    } else if (tags.some((t) => t.includes("vue"))) {
      categories["Vue"].push(project);
    } else if (tags.some((t) => t.includes("wordpress"))) {
      categories["Wordpress"].push(project);
    } else if (tags.some((t) => t.includes("tauri"))) {
      categories["Desktop"].push(project);
    } else if (tags.some((t) => t.includes("canvas") || t.includes("game"))) {
      categories["Games"].push(project);
    } else {
      categories["Other"].push(project);
    }
  });

  // Remove empty categories
  return Object.fromEntries(
    Object.entries(categories).filter(([_, projects]) => projects.length > 0)
  );
};

// Build tree elements structure
const buildTreeElements = (
  categories: Record<string, Project[]>,
  projectMap: Map<string, Project>
): TreeViewElement[] => {
  const elements: TreeViewElement[] = [];
  let idCounter = 1;

  Object.entries(categories).forEach(([category, categoryProjects]) => {
    const categoryId = `category-${idCounter++}`;
    const children: TreeViewElement[] = [];

    categoryProjects.forEach((project) => {
      const projectId = project.id;
      projectMap.set(projectId, project);
      children.push({
        id: projectId,
        name: `${project.name}.tsx`,
        isSelectable: true,
      });
    });

    elements.push({
      id: categoryId,
      name: category,
      isSelectable: true,
      children,
    });
  });

  return elements;
};

// Get all unique categories from projects (for mobile filter)
const getCategories = (projects: Project[]): string[] => {
  const categorySet = new Set<string>();
  projects.forEach((project) => {
    const tags = project.tags.map((t) => t.name.toLowerCase());
    if (tags.some((t) => t.includes("next"))) {
      categorySet.add("Next.js");
    } else if (tags.some((t) => t.includes("react") && !t.includes("next"))) {
      categorySet.add("React");
    } else if (tags.some((t) => t.includes("vue"))) {
      categorySet.add("Vue");
    } else if (tags.some((t) => t.includes("wordpress"))) {
      categorySet.add("Wordpress");
    } else if (tags.some((t) => t.includes("tauri"))) {
      categorySet.add("Desktop");
    } else if (tags.some((t) => t.includes("canvas") || t.includes("game"))) {
      categorySet.add("Games");
    } else {
      categorySet.add("Other");
    }
  });
  return Array.from(categorySet).sort();
};

// Filter projects by category
const filterProjectsByCategory = (projects: Project[], category: string | null): Project[] => {
  if (!category) return projects;
  
  return projects.filter((project) => {
    const tags = project.tags.map((t) => t.name.toLowerCase());
    switch (category) {
      case "Next.js":
        return tags.some((t) => t.includes("next"));
      case "React":
        return tags.some((t) => t.includes("react") && !t.includes("next"));
      case "Vue":
        return tags.some((t) => t.includes("vue"));
      case "Wordpress":
        return tags.some((t) => t.includes("wordpress"));
      case "Desktop":
        return tags.some((t) => t.includes("tauri"));
      case "Games":
        return tags.some((t) => t.includes("canvas") || t.includes("game"));
      default:
        return true;
    }
  });
};

export const ProjectTags = ({ tags }: { tags: Project["tags"] }) => (
  <div className="flex flex-wrap gap-2">
    {tags.map((tag) => (
      <span
        key={tag.name}
        className={cn(
          "rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest transition-all",
          tagStyles[tag.color]
        )}
      >
        {tag.name}
      </span>
    ))}
  </div>
);

export const ProjectActions = ({
  source,
  live,
  video,
}: {
  source?: string;
  live?: string;
  video?: string;
}) => {
  const actions = [
    live && { label: "Launch", href: live, icon: ExternalLink },
    video && { label: "Video", href: video, icon: Video },
    source && { label: "Code", href: source, icon: Github },
  ].filter(Boolean) as Array<{ label: string; href: string; icon: any }>;

  if (!actions.length) return null;

  return (
    <div className="flex flex-wrap gap-2 sm:gap-3">
      {actions.map((action) => (
        <Link
          key={action.label}
          href={action.href}
          target="_blank"
          rel="noreferrer noopener"
          className="group flex items-center gap-1.5 sm:gap-2 rounded-full border border-white/20 px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-white/90 transition-all hover:border-white/40 hover:bg-white/15 hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]"
        >
          <action.icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          {action.label}
        </Link>
      ))}
    </div>
  );
};

// Custom File component that tracks selection (for desktop file tree)
const ProjectFile = ({
  project,
  value,
  ...props
}: {
  project: Project;
  value: string;
  [key: string]: any;
}) => {
  const selectionContext = useContext(SelectionContext);

  const originalOnClick = props.onClick as ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    selectionContext?.setSelectedId(value);
    if (originalOnClick) {
      originalOnClick(e);
    }
  };

  return (
    <File
      value={value}
      fileIcon={<Code2 className="h-4 w-4 text-blue-400" />}
      className="text-white/95 hover:text-white hover:bg-white/15 transition-colors duration-200 font-medium [&_svg]:text-blue-400 [&_svg]:opacity-100"
      onClick={handleClick}
      {...props}
    >
      <span className="text-white/95">{project.name}.tsx</span>
    </File>
  );
};

// Desktop Project Detail Panel
const DesktopProjectDetailPanel = ({ project }: { project: Project | null }) => {
  if (!project) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.3 }}
        className="relative flex h-full flex-col overflow-hidden rounded-[16px] sm:rounded-[20px] md:rounded-2xl border border-white/20 bg-black/60 p-4 sm:p-5 md:p-6 backdrop-blur-xl"
      >
        {/* Glow effect */}
        <div className="pointer-events-none absolute -inset-1 rounded-[16px] sm:rounded-[20px] md:rounded-2xl bg-gradient-to-r from-blue-500/0 via-purple-500/30 to-amber-500/0 blur-xl opacity-50" />

        {/* Image */}
        <div className="relative mb-4 sm:mb-5 md:mb-6 h-48 sm:h-56 md:h-64 overflow-hidden rounded-[12px] sm:rounded-[16px] md:rounded-xl">
          <Image
            src={project.image}
            alt={project.name}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 500px"
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 space-y-3 sm:space-y-4">
          <div>
            <p className="mb-1 sm:mb-2 text-[10px] sm:text-xs uppercase tracking-[0.4em] text-white/50">
              Project Details
            </p>
            <h3 className="bg-gradient-to-r from-white via-white/80 to-white/60 bg-clip-text text-xl sm:text-2xl font-semibold">
              {project.name}
            </h3>
          </div>

          <p className="text-xs sm:text-sm leading-relaxed text-white/70">
            {project.description}
          </p>

          <div>
            <p className="mb-1 sm:mb-2 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/50">
              Tech Stack
            </p>
            <ProjectTags tags={project.tags} />
          </div>
        </div>

        {/* Actions at bottom */}
        <div className="relative z-10 mt-auto space-y-3 sm:space-y-4 pt-4 sm:pt-5 md:pt-6">
          {project.hasDetailPage && (
            <Link
              href={`/projects/${project.id}`}
              className="group flex w-full items-center justify-center gap-2 rounded-full border border-[#ffe36c]/30 bg-gradient-to-r from-[#ffe36c]/10 to-[#c58bff]/10 px-4 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] text-white transition-all hover:border-[#ffe36c]/50 hover:from-[#ffe36c]/20 hover:to-[#c58bff]/20 hover:shadow-[0_0_20px_rgba(255,227,108,0.3)]"
            >
              <span>View Full Details</span>
              <ExternalLink className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          )}
          <ProjectActions
            source={project.source_code_link}
            live={project.deployed_site_link}
            video={project.video_demo_link}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Project Card Component
const ProjectCard = ({
  project,
  index,
  onClick,
}: {
  project: Project;
  index: number;
  onClick: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative cursor-pointer"
      onClick={onClick}
    >
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl transition-all duration-300 hover:border-white/20 hover:bg-black/60 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        {/* Glow effect on hover */}
        <div className="pointer-events-none absolute -inset-0.5 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-blue-500/0 via-purple-500/0 to-amber-500/0 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-50 group-hover:from-blue-500/20 group-hover:via-purple-500/20 group-hover:to-amber-500/20" />
        
        {/* Image */}
        <div className="relative aspect-video overflow-hidden">
          <Image
            src={project.image}
            alt={project.name}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover transition-transform duration-500 group-hover:scale-110"
          />
          {/* Darker gradient overlay for better text visibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
          
          {/* Overlay content */}
          <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-1 sm:mb-2 drop-shadow-lg">
              {project.name}
            </h3>
            <ProjectTags tags={project.tags.slice(0, 3)} />
          </div>
        </div>

        {/* Description */}
        <div className="p-4 sm:p-6">
          <p className="text-xs sm:text-sm text-white/70 line-clamp-2 leading-relaxed">
            {project.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Full-screen Modal for Project Details
const ProjectModal = ({
  project,
  isOpen,
  onClose,
}: {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!project) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-4 sm:inset-8 md:inset-12 lg:inset-16 z-50 overflow-y-auto"
          >
            <div className="relative h-full max-w-5xl mx-auto rounded-2xl sm:rounded-3xl border border-white/20 bg-black/80 backdrop-blur-2xl overflow-hidden">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute right-4 top-4 sm:right-6 sm:top-6 z-50 rounded-full p-2 sm:p-3 bg-black/60 border border-white/20 text-white/70 transition-all hover:bg-white/10 hover:text-white hover:border-white/40"
                aria-label="Close"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>

              {/* Hero Image */}
              <div className="relative h-48 sm:h-64 md:h-80 overflow-hidden">
                <Image
                  src={project.image}
                  alt={project.name}
                  fill
                  sizes="100vw"
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                
                {/* Title overlay */}
                <div className="absolute inset-0 flex items-end p-6 sm:p-8 md:p-10">
                  <div>
                    <p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-white/50 mb-2">
                      Project Details
                    </p>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-3 sm:mb-4">
                      {project.name}
                    </h2>
                    <ProjectTags tags={project.tags} />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8 md:p-10 space-y-6 sm:space-y-8">
                {/* Description */}
                <div>
                  <p className="text-sm sm:text-base leading-relaxed text-white/80">
                    {project.description}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  {project.hasDetailPage && (
                    <Link
                      href={`/projects/${project.id}`}
                      className="group flex-1 flex items-center justify-center gap-2 rounded-full border border-[#ffe36c]/30 bg-gradient-to-r from-[#ffe36c]/10 to-[#c58bff]/10 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold uppercase tracking-[0.15em] text-white transition-all hover:border-[#ffe36c]/50 hover:from-[#ffe36c]/20 hover:to-[#c58bff]/20 hover:shadow-[0_0_20px_rgba(255,227,108,0.3)]"
                    >
                      <span>View Full Details</span>
                      <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  )}
                  <ProjectActions
                    source={project.source_code_link}
                    live={project.deployed_site_link}
                    video={project.video_demo_link}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const Works = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = useMemo(() => getCategories(projects), []);
  const filteredProjects = useMemo(
    () => filterProjectsByCategory(projects, selectedCategory),
    [selectedCategory]
  );
  
  // For desktop file tree
  const projectMap = useMemo(() => {
    const map = new Map<string, Project>();
    projects.forEach((p) => map.set(p.id, p));
    return map;
  }, []);

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Small delay before clearing project for smooth exit animation
    setTimeout(() => setSelectedProject(null), 300);
  };

  return (
    <Section id="works" label="Works" order={4}>
      {/* Header */}
      <motion.div
        variants={staggerContainer(0.1, 0.2)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        className={cn(
          "relative mb-8 sm:mb-10 md:mb-12 rounded-[24px] sm:rounded-[32px] border border-white/10 bg-black/30 px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10 shadow-[0_25px_80px_rgba(5,7,19,0.65)] backdrop-blur-xl",
          styles.commonShell
        )}
      >
        <motion.div variants={textVariant()} className="relative z-10">
          <p className="sectionSubText">Mission archive</p>
          <h2 className="sectionHeadText">Project Repository</h2>
        </motion.div>

        <motion.p
          variants={fadeIn("", "", 0.1, 1)}
          className="paragraphText relative z-10 mt-4 max-w-4xl text-white/75 text-sm sm:text-base md:text-lg"
        >
          {projectsIntro}
        </motion.p>
      </motion.div>

      {/* Mobile: Filter Chips & Card Grid */}
      <div className="lg:hidden">
        {/* Filter Chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 text-white/60">
              <Filter className="h-4 w-4" />
              <span className="text-xs sm:text-sm uppercase tracking-[0.2em]">Filter:</span>
            </div>
            <button
              onClick={() => setSelectedCategory(null)}
              className={cn(
                "rounded-full border px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] transition-all",
                selectedCategory === null
                  ? "border-white/40 bg-white/10 text-white shadow-[0_0_15px_rgba(255,255,255,0.2)]"
                  : "border-white/10 bg-black/40 text-white/70 hover:border-white/20 hover:text-white"
              )}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  "rounded-full border px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] transition-all",
                  selectedCategory === category
                    ? "border-white/40 bg-white/10 text-white shadow-[0_0_15px_rgba(255,255,255,0.2)]"
                    : "border-white/10 bg-black/40 text-white/70 hover:border-white/20 hover:text-white"
                )}
              >
                {category}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Projects Grid */}
        <div className="relative">
          <motion.div
            className={cn(
              "relative mx-auto w-full max-w-7xl overflow-hidden rounded-[24px] sm:rounded-[32px] backdrop-blur-2xl",
              styles.projectShell
            )}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className={styles.starfield} aria-hidden="true" />
            <div className={styles.nebula} aria-hidden="true" />

            <div className="relative p-4 sm:p-6">
              {filteredProjects.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {filteredProjects.map((project, index) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      index={index}
                      onClick={() => handleProjectClick(project)}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-16 sm:py-24">
                  <p className="text-sm sm:text-base text-white/50 uppercase tracking-[0.2em]">
                    No projects found
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Desktop: File Tree Layout */}
      <div className="hidden lg:block">
        <div className="relative">
          <motion.div
            className={cn(
              "relative mx-auto w-full max-w-7xl overflow-hidden rounded-[24px] sm:rounded-[32px] md:rounded-[40px] lg:rounded-[48px] backdrop-blur-2xl",
              styles.projectShell
            )}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className={styles.starfield} aria-hidden="true" />
            <div className={styles.nebula} aria-hidden="true" />

            <div className="grid gap-4 sm:gap-6 md:gap-8 lg:grid-cols-[1fr_1.2fr]">
              {/* File Tree Panel */}
              <motion.div
                variants={fadeIn("left", "spring", 0.2, 0.8)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-[16px] sm:rounded-[20px] md:rounded-2xl border border-white/20 bg-black/60 p-3 sm:p-4 md:p-6 backdrop-blur-xl"
              >
                {/* Terminal-style header */}
                <div className="mb-4 sm:mb-6 flex items-center gap-2 border-b border-white/20 pb-3 sm:pb-4">
                  <div className="flex gap-1 sm:gap-1.5">
                    <div className="h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full bg-red-500" />
                    <div className="h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full bg-yellow-500" />
                    <div className="h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full bg-green-500" />
                  </div>
                  <p className="ml-2 sm:ml-4 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/70 font-semibold">
                    projects/
                  </p>
                </div>

                {/* File Tree */}
                <SelectionContext.Provider
                  value={{ selectedId: selectedProject?.id, setSelectedId: (id) => setSelectedProject(projects.find(p => p.id === id) ?? null) }}
                >
                  <div className="file-tree-wrapper [&_button]:text-white/95 [&_button:hover]:text-white [&_button:hover]:bg-white/15 [&_.bg-muted]:!bg-white/20 [&_button]:transition-all [&_svg]:opacity-100">
                    <Tree
                      className="h-[400px] sm:h-[500px] md:h-[600px] overflow-hidden"
                      initialSelectedId={selectedProject?.id}
                      initialExpandedItems={Object.keys(organizeProjectsByTech(projects)).map(
                        (_, idx) => `category-${idx + 1}`
                      )}
                      elements={buildTreeElements(organizeProjectsByTech(projects), new Map(projects.map(p => [p.id, p])))}
                    >
                      {Object.entries(organizeProjectsByTech(projects)).map(([category, categoryProjects], categoryIdx) => (
                        <Folder
                          key={category}
                          element={category}
                          value={`category-${categoryIdx + 1}`}
                          className={cn(
                            "text-white/90 hover:text-white hover:bg-white/10 [&_svg]:text-amber-400 [&_svg]:opacity-100 font-medium transition-colors [&_span]:text-white/90",
                            styles.projectFileTree
                          )}
                        >
                          {categoryProjects.map((project) => (
                            <ProjectFile
                              key={project.id}
                              project={project}
                              value={project.id}
                              className={styles.projectFileTreeFile}
                            />
                          ))}
                        </Folder>
                      ))}
                    </Tree>
                  </div>
                </SelectionContext.Provider>
              </motion.div>

              {/* Project Detail Panel */}
              <motion.div
                variants={fadeIn("right", "spring", 0.4, 0.8)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="relative h-full min-h-[400px] sm:min-h-[500px] md:min-h-[600px]"
              >
                {selectedProject ? (
                  <DesktopProjectDetailPanel project={selectedProject} />
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex h-full items-center justify-center rounded-[16px] sm:rounded-[20px] md:rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl p-4"
                  >
                    <div className="text-center">
                      <Code2 className="mx-auto mb-4 h-8 w-8 sm:h-12 sm:w-12 text-white/20" />
                      <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-white/40">
                        Select a project to view details
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Project Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </Section>
  );
};

export default Works;
